import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { parseEventDate } from '../../utils/eventDate';

interface PublicPhoto { id: string; filePath: string; fileName: string; }
interface PublicAudio { id: string; filePath: string; label: string | null; durationSec: number | null; }
interface PublicEvent {
  id: string; title: string; description: string | null;
  eventDate: string | null; location: string | null;
  photos: PublicPhoto[]; audioClips: PublicAudio[];
}

export function PublicEventPage() {
  const { token } = useParams<{ token: string }>();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    axios.get(`/api/events/public/${token}`)
      .then(r => setEvent(r.data))
      .catch(() => setNotFound(true));
  }, [token]);

  useEffect(() => {
    if (!lightbox) return;
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [lightbox]);

  if (notFound) return (
    <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center p-4">
      <div className="text-center">
        <p className="text-slate-500 mb-4">This event link is no longer available.</p>
        <Link to="/login" className="text-indigo-600 hover:underline text-sm">Sign in to EventsCarousel</Link>
      </div>
    </div>
  );

  if (!event) return (
    <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center">
      <div className="w-6 h-6 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
    </div>
  );

  const dateStr = event.eventDate ? format(parseEventDate(event.eventDate), 'MMMM d, yyyy') : null;

  return (
    <div className="min-h-screen bg-[#F8F9FF]">
      {/* Header */}
      <header className="bg-white border-b border-indigo-50 sticky top-0 z-40">
        <div className="px-4 lg:px-8 h-[62px] flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-[34px] h-[34px] rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
              </svg>
            </div>
            <span className="text-[1.05rem] font-bold tracking-tight text-slate-900">EventsCarousel</span>
          </Link>
          <Link to="/register" className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
            Join EventsCarousel
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 lg:px-8 lg:py-10">
        {/* Event header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{event.title}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-sm text-slate-500">
            {dateStr && <span>{dateStr}</span>}
            {event.location && <span>{event.location}</span>}
          </div>
          {event.description && (
            <p className="text-slate-600 mt-4 leading-relaxed whitespace-pre-wrap">{event.description}</p>
          )}
        </div>

        {/* Photos */}
        {event.photos.length > 0 && (
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-700 mb-4">Photos</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {event.photos.map(photo => (
                <div key={photo.id} className="rounded-xl overflow-hidden bg-white shadow-sm border border-slate-100 aspect-square">
                  <img
                    src={photo.filePath}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover cursor-zoom-in hover:scale-105 transition-transform duration-200"
                    onClick={() => setLightbox(photo.filePath)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Audio */}
        {event.audioClips.length > 0 && (
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-700 mb-4">Voice Stories</h2>
            <div className="space-y-3">
              {event.audioClips.map(clip => (
                <div key={clip.id} className="bg-indigo-50 rounded-xl p-3">
                  {clip.label && <p className="text-sm font-medium text-slate-700 mb-1">{clip.label}</p>}
                  <audio src={clip.filePath} controls className="w-full h-8" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="bg-white rounded-2xl border border-indigo-100 p-6 text-center">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Preserve your own family memories</h3>
          <p className="text-sm text-slate-500 mb-4">EventsCarousel is a private archive for photos, voices, and stories — shared only with the people you choose.</p>
          <Link to="/register" className="inline-block px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200">
            Create your free archive
          </Link>
        </div>
      </main>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="" className="max-w-[95vw] max-h-[95vh] object-contain cursor-zoom-out select-none" />
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl flex items-center justify-center"
          >×</button>
        </div>
      )}
    </div>
  );
}
